import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { FileArchive, ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/galleryApi";
import { useChunkedUpload } from "@/hooks/useChunkedUpload";
import { formatBytes } from "@/components/gallery/PurgeDialog";
import { fmtDate } from "@/components/gallery/retentionUi";

const STATE = { exists: "La galerie d'origine existe : les fichiers manquants y seront reintegres", deleted: "La galerie d'origine a ete supprimee : une nouvelle galerie privee sera creee", missing: "Galerie inconnue de cette installation : une nouvelle galerie privee sera creee" };

function Choice({ value, current, onChange, title, desc, testId }) {
  const on = value === current;
  return (
    <button type="button" onClick={() => onChange(value)} data-testid={testId}
      className={`text-left w-full rounded-lg border px-3 py-2.5 transition-colors ${on ? "border-[#0099CC] bg-[#0099CC]/10" : "border-[#1A2332] hover:border-[#334155]"}`}>
      <span className="block text-sm text-white">{title}</span><span className="block text-xs text-[#64748B] mt-0.5">{desc}</span>
    </button>
  );
}

function Analysis({ a }) {
  const items = [
    ["Galerie", a.gallery?.title], ["Organisation", a.gallery?.org_name || a.gallery?.org_id], ["Evenement", a.gallery?.event_title || "—"],
    ["Archive", `creee le ${fmtDate(a.archive_created_at)} par ${a.archive_created_by || "—"}`], ["Partie", `${a.part} sur ${a.parts}`],
    ["Fichiers verifies", `${a.files_ok} / ${a.files_total} (${a.originals} originaux, ${a.processed} retouches)`], ["Taille", formatBytes(a.bytes)],
    ["Photos a restaurer", `${a.to_restore} (deja presentes : ${a.already_present})`], ["Selection du responsable", a.keep_count ? `${a.keep_count} photos` : "aucune"],
  ];
  return (
    <div className="space-y-3" data-testid="restore-analysis">
      <div className="flex items-center gap-2 text-sm text-[#10B981]"><ShieldCheck className="w-4 h-4" />Signature, empreintes SHA-256 et contenu verifies</div>
      <div className="bg-[#060B14] border border-[#1A2332] rounded-lg divide-y divide-[#1A2332]">
        {items.map(([k, v]) => <div key={k} className="flex justify-between gap-3 px-3 py-1.5 text-xs"><span className="text-[#64748B]">{k}</span><span className="text-white text-right">{v}</span></div>)}
      </div>
      <p className="text-xs text-[#94A3B8]" data-testid="restore-gallery-state">{STATE[a.gallery_state]}</p>
      {a.diffs?.length > 0 && <p className="text-xs text-[#F59E0B]">Parametres modifies depuis l'archive ({a.diffs.map((d) => d.field).join(", ")}) : les reglages actuels, plus recents, sont conserves.</p>}
      {a.warnings?.map((w) => <p key={w} className="text-xs text-[#F59E0B] flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}</p>)}
    </div>
  );
}

function Report({ job, onOpen }) {
  const r = job.report;
  return (
    <div className="space-y-3" data-testid="restore-result">
      <p className="text-sm">{job.status === "done" ? "Restauration terminee" : job.status === "partial" ? "Restauration partielle" : "Echec de la restauration"} : {job.restored} photo(s) restauree(s), {job.skipped} deja presente(s){job.errors ? `, ${job.errors} erreur(s)` : ""}.</p>
      {r && (
        <div className="bg-[#060B14] border border-[#1A2332] rounded-lg p-3 space-y-1.5" data-testid="restore-test-report">
          <p className={`text-sm font-semibold ${r.ok ? "text-[#10B981]" : "text-red-400"}`}>{r.ok ? "Test de restauration reussi" : "Test de restauration en echec"}</p>
          {r.checks.map((c) => (
            <div key={c.label} className="flex items-start gap-2 text-xs">
              {c.ok === false ? <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" /> : <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${c.ok ? "text-[#10B981]" : "text-[#64748B]"}`} />}
              <span className="text-white">{c.label}</span><span className="text-[#64748B] ml-auto text-right">{c.detail}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-[#64748B]">{job.mode === "test" ? "La galerie de test est privee et invisible des clients. Supprimez-la apres verification." : "La galerie reste privee et ses anciens liens ne sont pas reactives. Verifiez les photos avant toute nouvelle mise a disposition."}</p>
      <Button onClick={onOpen} className="w-full bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="restore-open-gallery">Ouvrir la galerie {job.mode === "test" ? "de test" : "restauree"}</Button>
    </div>
  );
}

export function RestoreDialog({ open, onOpenChange, galleryId, onDone }) {
  const navigate = useNavigate();
  const { upload, progress, cancel } = useChunkedUpload();
  const [step, setStep] = useState("file");
  const [up, setUp] = useState(null);
  const [mode, setMode] = useState("test");
  const [publish, setPublish] = useState("none");
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [job, setJob] = useState(null);
  const a = up?.analysis;

  useEffect(() => { if (open) { setStep("file"); setUp(null); setJob(null); setMode("test"); setPublish("none"); } }, [open]);
  useEffect(() => { if (a && a.gallery_state !== "exists" && !a.org_exists) api.get("/event/admin/organizations").then((o) => setOrgs(Array.isArray(o) ? o : o.organizations || [])).catch(() => {}); }, [a]);
  useEffect(() => {
    if (step !== "running" || !job) return undefined;
    const t = setInterval(async () => {
      const j = await api.get(`/galleries/jobs/${job.id}`).catch(() => null);
      if (j) setJob(j);
      if (j && j.status !== "running") { setStep("result"); onDone?.(); }
    }, 1500);
    return () => clearInterval(t);
  }, [step, job, onDone]);

  const pick = async (file) => {
    if (!file) return;
    try {
      setStep("uploading");
      const u = await upload(file, galleryId);
      setStep("analyzing");
      const res = await api.post(`/gallery-restore/uploads/${u.id}/analyze`, {});
      setUp(res);
      setStep("preview");
    } catch (e) { toast.error(errMsg(e)); setStep("file"); }
  };

  const start = async () => {
    try {
      const j = await api.post(`/gallery-restore/uploads/${up.id}/restore`, { mode, publish, org_id: orgId || undefined });
      setJob(j);
      setStep("running");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const close = (o) => { if (!o && step === "uploading") cancel(); onOpenChange(o); };
  const pct = progress ? Math.round((progress.sent / progress.total) * 100) : 0;
  const needOrg = a?.ok && mode === "restore" && a.gallery_state !== "exists" && !a.org_exists;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#0B1120] border-[#1A2332] text-white w-[calc(100vw-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle className="text-left flex items-center gap-2"><FileArchive className="w-5 h-5 text-[#F97316]" />Restaurer depuis une archive ZIP</DialogTitle>
          <DialogDescription className="text-[#94A3B8] text-left">Importez une partie d'archive GLOTECH conservee sur votre ordinateur ou votre NAS. Rien n'est publie automatiquement.</DialogDescription>
        </DialogHeader>
        {step === "file" && (
          <label className="block border-2 border-dashed border-[#1A2332] hover:border-[#0099CC]/60 rounded-xl py-10 text-center cursor-pointer transition-colors" data-testid="restore-dropzone">
            <FileArchive className="w-10 h-10 text-[#334155] mx-auto mb-3" />
            <span className="text-sm text-white">Choisir une partie d'archive (.zip)</span>
            <span className="block text-xs text-[#64748B] mt-1">Envoi par morceaux, reprise automatique en cas de coupure</span>
            <input type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="restore-file-input" />
          </label>
        )}
        {step === "uploading" && <div className="space-y-2 py-4" data-testid="restore-uploading"><p className="text-sm">Envoi de l'archive… {pct} %</p><Progress value={pct} /><p className="text-xs text-[#64748B]">{progress && `${formatBytes(progress.sent)} / ${formatBytes(progress.total)}`}</p></div>}
        {(step === "analyzing" || step === "running") && (
          <div className="py-6 text-center space-y-3" data-testid={`restore-${step}`}>
            <Loader2 className="w-7 h-7 animate-spin text-[#0099CC] mx-auto" />
            <p className="text-sm">{step === "analyzing" ? "Analyse de securite et verification des empreintes…" : `Restauration en cours… ${job?.done || 0} / ${job?.total || 0}`}</p>
            {step === "running" && job?.total > 0 && <Progress value={(100 * (job.done || 0)) / job.total} />}
          </div>
        )}
        {step === "preview" && a && !a.ok && (
          <div className="space-y-2" data-testid="restore-rejected">
            <p className="text-sm text-red-400 flex items-center gap-2"><XCircle className="w-4 h-4" />Archive refusee</p>
            {a.errors.map((e) => <p key={e} className="text-xs text-[#94A3B8]">{e}</p>)}
            <Button variant="outline" onClick={() => setStep("file")} className="w-full bg-transparent border-[#1A2332] text-white mt-2">Choisir une autre archive</Button>
          </div>
        )}
        {step === "preview" && a?.ok && (
          <div className="space-y-4">
            <Analysis a={a} />
            {galleryId && a.gallery?.id !== galleryId && <p className="text-xs text-[#F59E0B]">Cette archive concerne une autre galerie : « {a.gallery?.title} ».</p>}
            <div className="space-y-2">
              <Choice value="test" current={mode} onChange={setMode} title="Test de restauration" desc="Import dans une galerie de test privee puis rapport de controle (empreintes, selection, parametres)" testId="restore-mode-test" />
              <Choice value="restore" current={mode} onChange={setMode} title="Restauration reelle" desc="Reintegre les photos manquantes sans ecraser les modifications plus recentes" testId="restore-mode-real" />
            </div>
            {mode === "restore" && (
              <div className="space-y-2">
                <Choice value="none" current={publish} onChange={setPublish} title="Restaurer sans publier" desc="Les photos restent invisibles pour le client" testId="restore-publish-none" />
                <Choice value="prepare" current={publish} onChange={setPublish} title="Restaurer et preparer la publication" desc="Les photos publiees a l'origine sont marquees « a republier » apres verification" testId="restore-publish-prepare" />
              </div>
            )}
            {needOrg && (
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className="w-full bg-[#060B14] border border-[#1A2332] rounded-md h-9 px-2 text-sm" data-testid="restore-org-select">
                <option value="">Organisation cliente…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.org_name || o.name}</option>)}
              </select>
            )}
            <Button onClick={start} disabled={needOrg && !orgId} className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white" data-testid="restore-confirm-btn">{mode === "test" ? "Lancer le test de restauration" : "Confirmer la restauration"}</Button>
          </div>
        )}
        {step === "result" && job && <Report job={job} onOpen={() => { onOpenChange(false); navigate(`/app/galeries/${job.gallery_id}`); }} />}
      </DialogContent>
    </Dialog>
  );
}
